import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Truck, Store, MapPin } from "lucide-react";
import SEOHead from "../components/SEOHead";
import { useCart, formatMoney } from "../lib/cart";
import { getLocation } from "../lib/api";

export default function Checkout() {
    const { storeSlug } = useParams();
    const nav = useNavigate();
    const {
        checkout,
        loading,
        updateOrderType,
        updateAddress,
    } = useCart();
    const [store, setStore] = useState(null);
    const [localOrderType, setLocalOrderType] = useState(null);
    const [addr, setAddr] = useState({
        street1: "",
        street2: "",
        city: "",
        state: "NY",
        zip: "",
    });
    const [savingAddr, setSavingAddr] = useState(false);
    const [addrError, setAddrError] = useState("");

    useEffect(() => {
        getLocation(storeSlug).then(setStore).catch(() => {});
    }, [storeSlug]);

    useEffect(() => {
        if (checkout?.address) {
            const addr = checkout.address;
            Promise.resolve().then(() => {
                setAddr((a) => ({
                    ...a,
                    street1: addr.street1 || "",
                    street2: addr.street2 || "",
                    city: addr.city || "",
                    state: addr.state || "NY",
                    zip: addr.zip || "",
                }));
            });
        }
    }, [checkout?.id]);

    if (!checkout || (checkout.items || []).length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Your cart is empty.
                </h1>
                <Link
                    to={`/menu/${storeSlug}`}
                    className="text-zen-olive underline"
                >
                    Browse the menu
                </Link>
            </div>
        );
    }

    const ps = checkout.priceSummary;
    const orderType = localOrderType || checkout.orderType || "PICKUP";
    const isDelivery = orderType === "DELIVERY";

    const setOrderType = (ot) => {
        setLocalOrderType(ot); // optimistic UI
        updateOrderType(ot).catch(() => {});
    };

    const saveAddress = async () => {
        setSavingAddr(true);
        setAddrError("");
        try {
            await updateAddress(addr);
        } catch (e) {
            setAddrError(
                e?.response?.data?.detail || "Couldn't save that address. Try again."
            );
        } finally {
            setSavingAddr(false);
        }
    };

    const goToFinalCheckout = () => {
        if (checkout.redirectUrl) {
            window.location.href = checkout.redirectUrl;
        }
    };

    const canProceed =
        !isDelivery || (checkout.address?.valid && checkout.address?.deliverable);

    return (
        <>
            <SEOHead
                title={`Checkout — ZenZest ${store?.short_name || ""}`}
                description="Complete your ZenZest cannabis order."
                canonical={`https://weeddispensarynearme.com/checkout/${storeSlug}`}
            />
            <section className="bg-zen-bg max-w-[1200px] mx-auto px-6 sm:px-10 py-12 sm:py-12">
                <Link
                    to={`/menu/${storeSlug}`}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink mb-8"
                >
                    <ArrowLeft size={12} /> Keep shopping
                </Link>
                <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4">
                    Step 1 of 2
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl font-light text-zen-ink leading-tight mb-8">
                    How would you like to receive your order?
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7 space-y-10">
                        {/* Order type */}
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                                Order type
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <OrderTypeCard
                                    icon={<Store size={20} />}
                                    label="In-store pickup"
                                    sub={`At ${store?.short_name || "the store"}`}
                                    active={orderType === "PICKUP"}
                                    onClick={() => setOrderType("PICKUP")}
                                    testid="ot-pickup"
                                />
                                <OrderTypeCard
                                    icon={<Truck size={20} />}
                                    label="Delivery"
                                    sub="Free on orders $100+"
                                    active={orderType === "DELIVERY"}
                                    onClick={() => setOrderType("DELIVERY")}
                                    testid="ot-delivery"
                                />
                            </div>
                        </div>

                        {/* Address (only for delivery) */}
                        {isDelivery && (
                            <div data-testid="delivery-address-form">
                                <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4 inline-flex items-center gap-2">
                                    <MapPin size={11} /> Delivery address
                                </p>
                                <div className="space-y-3">
                                    <Input
                                        testid="addr-street1"
                                        placeholder="Street address"
                                        value={addr.street1}
                                        onChange={(v) =>
                                            setAddr((a) => ({ ...a, street1: v }))
                                        }
                                    />
                                    <Input
                                        testid="addr-street2"
                                        placeholder="Apt, suite, etc. (optional)"
                                        value={addr.street2}
                                        onChange={(v) =>
                                            setAddr((a) => ({ ...a, street2: v }))
                                        }
                                    />
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <Input
                                            testid="addr-city"
                                            placeholder="City"
                                            value={addr.city}
                                            onChange={(v) =>
                                                setAddr((a) => ({ ...a, city: v }))
                                            }
                                            cols="col-span-2"
                                        />
                                        <Input
                                            testid="addr-state"
                                            placeholder="State"
                                            value={addr.state}
                                            onChange={(v) =>
                                                setAddr((a) => ({ ...a, state: v.toUpperCase().slice(0, 2) }))
                                            }
                                        />
                                        <Input
                                            testid="addr-zip"
                                            placeholder="ZIP"
                                            value={addr.zip}
                                            onChange={(v) =>
                                                setAddr((a) => ({ ...a, zip: v }))
                                            }
                                        />
                                    </div>
                                    <button
                                        onClick={saveAddress}
                                        data-testid="addr-save"
                                        disabled={savingAddr || !addr.street1 || !addr.zip}
                                        className="text-[11px] uppercase tracking-[0.25em] text-zen-bg bg-zen-ink hover:bg-zen-elevated disabled:opacity-50 px-6 py-3 transition-colors"
                                    >
                                        {savingAddr ? "Saving…" : "Save address"}
                                    </button>
                                    {checkout.address?.formatted && (
                                        <p
                                            className={`text-xs mt-2 ${
                                                checkout.address.deliverable
                                                    ? "text-zen-olive"
                                                    : "text-red-600"
                                            }`}
                                        >
                                            {checkout.address.formatted}
                                            {checkout.address.deliverable
                                                ? " · ✓ In delivery zone"
                                                : " · ✗ Outside delivery zone"}
                                        </p>
                                    )}
                                    {addrError && (
                                        <p
                                            data-testid="addr-error"
                                            className="text-xs mt-2 text-red-600"
                                        >
                                            {addrError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    <aside className="lg:col-span-5">
                        <div className="bg-zen-surface border border-zen-ink/10 p-7 sm:p-8 sticky top-24">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5">
                                Order summary
                            </p>
                            <ul className="space-y-4 mb-6 max-h-[260px] overflow-auto pr-1">
                                {checkout.items.map((it) => (
                                    <li
                                        key={it.id}
                                        className="flex justify-between gap-4 text-sm"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-zen-ink leading-tight truncate">
                                                {it.product?.name}
                                            </p>
                                            <p className="text-[11px] text-zen-ink-soft">
                                                {it.option} × {it.quantity}
                                            </p>
                                        </div>
                                        <span className="font-mono text-zen-ink whitespace-nowrap">
                                            {it.basePrice
                                                ? formatMoney(it.basePrice)
                                                : "—"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div className="space-y-2 border-t border-zen-ink/10 pt-4 text-sm">
                                <Row label="Subtotal" value={formatMoney(ps?.subtotal)} />
                                {ps?.discounts ? (
                                    <Row
                                        label="Discounts"
                                        value={`− ${formatMoney(ps.discounts)}`}
                                        tone="olive"
                                    />
                                ) : null}
                                <Row label="Tax" value={formatMoney(ps?.taxes)} />
                                <Row
                                    label="Total"
                                    value={formatMoney(ps?.total)}
                                    strong
                                />
                            </div>
                            <button
                                onClick={goToFinalCheckout}
                                disabled={!canProceed || loading || !checkout.redirectUrl}
                                data-testid="checkout-pay-cta"
                                className="w-full mt-6 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg py-4 font-medium uppercase tracking-[0.25em] text-[11px] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continue to payment <ArrowUpRight size={14} />
                            </button>
                            <p className="text-[10px] text-zen-muted text-center mt-4 leading-relaxed">
                                Step 2: ID verification & payment on
                                ZenZest&rsquo;s secured checkout — required by
                                NY OCM for all cannabis purchases.
                            </p>
                        </div>
                    </aside>
                </div>
            </section>
        </>
    );
}

function OrderTypeCard({ icon, label, sub, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`text-left p-6 border transition-all ${
                active
                    ? "border-zen-olive bg-zen-olive/5"
                    : "border-zen-ink/15 hover:border-zen-ink/50"
            }`}
        >
            <div
                className={`mb-4 ${
                    active ? "text-zen-olive" : "text-zen-ink-soft"
                }`}
            >
                {icon}
            </div>
            <p className="font-serif text-xl text-zen-ink font-light leading-tight mb-1">
                {label}
            </p>
            <p className="text-[11px] text-zen-ink-soft">{sub}</p>
        </button>
    );
}

function Input({ value, onChange, placeholder, testid, cols = "" }) {
    return (
        <input
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`bg-white border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink placeholder:text-zen-muted px-4 py-3 text-sm focus:outline-none transition-colors w-full ${cols}`}
        />
    );
}

function Row({ label, value, strong = false, tone }) {
    return (
        <div className="flex justify-between items-baseline">
            <span className="text-zen-ink-soft uppercase tracking-[0.2em] text-[10px]">
                {label}
            </span>
            <span
                className={`${
                    strong
                        ? "font-serif text-2xl text-zen-ink font-light"
                        : "text-sm"
                } ${tone === "olive" ? "text-zen-olive" : ""}`}
            >
                {value}
            </span>
        </div>
    );
}
